import React from "react";
import { useTranslation } from "react-i18next";
import { DatePicker, Input, InputNumber, Select, Switch } from "antd";
import dayjs from "dayjs";
import styles from "./style.module.scss";

const { TextArea } = Input;

// The time zones offered for a deadline; the owner's own one is added when it is missing.
const TIME_ZONES = [
  "Europe/Istanbul",
  "UTC",
  "Europe/London",
  "Europe/Berlin",
  "Europe/Paris",
  "Europe/Madrid",
  "Europe/Rome",
  "Asia/Dubai",
  "Asia/Riyadh",
  "America/New_York",
  "America/Chicago",
  "America/Los_Angeles",
];

export const DEFAULT_CLOSE_TIMEZONE = "Europe/Istanbul";
export const DEFAULT_GRACE_MINUTES = 60;

const zoneOptions = (current) => {
  let own = "";
  try {
    own = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch (_) {
    own = "";
  }
  const zones = [...TIME_ZONES];
  [own, current].forEach((zone) => {
    if (zone && !zones.includes(zone)) zones.push(zone);
  });
  return zones.map((zone) => ({ value: zone, label: zone.replace(/_/g, " ") }));
};

/**
 * "Closing" settings of a survey (see pivony-api survey_close): a deadline in a chosen time zone,
 * a response limit, the message the link shows once closed, the deadline on the start screen, the
 * grace period for people who started before the deadline, and a reminder 24 hours before.
 */
const CloseSettings = ({ form, setField, error }) => {
  const { t } = useTranslation();
  const on = !!form.closeEnabled;
  return (
    <div className={styles.builderSection}>
      <div className={styles.settingRow}>
        <div className={styles.settingRowText}>
          <span className={styles.fieldLabel}>{t("surveyManagement.close.title")}</span>
          <span className={styles.fieldHint}>{t("surveyManagement.close.hint")}</span>
        </div>
        <Switch checked={on} onChange={(checked) => setField("closeEnabled", checked)} />
      </div>
      {on ? (
        <div className={styles.closeFields}>
          {form.autoClosed ? <p className={styles.closeNotice}>{t("surveyManagement.close.closed_notice")}</p> : null}
          <label className={styles.fieldLabel}>{t("surveyManagement.close.deadline")}</label>
          <div className={styles.closeRow}>
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="YYYY-MM-DD HH:mm"
              value={form.closeLocal ? dayjs(form.closeLocal) : null}
              onChange={(value) => setField("closeLocal", value ? value.format("YYYY-MM-DDTHH:mm") : "")}
              placeholder={t("surveyManagement.close.deadline_placeholder")}
              status={error ? "error" : ""}
            />
            <Select
              value={form.closeTimezone || DEFAULT_CLOSE_TIMEZONE}
              onChange={(zone) => setField("closeTimezone", zone)}
              options={zoneOptions(form.closeTimezone)}
              showSearch
              style={{ minWidth: 200 }}
              aria-label={t("surveyManagement.close.timezone")}
            />
          </div>
          {error ? <div className={styles.fieldError}>{error}</div> : null}

          <label className={styles.fieldLabel}>{t("surveyManagement.close.max_responses")}</label>
          <InputNumber
            min={1}
            value={form.closeMaxResponses || null}
            onChange={(value) => setField("closeMaxResponses", value || null)}
            placeholder={t("surveyManagement.close.no_limit")}
            style={{ width: 160 }}
          />

          <label className={styles.fieldLabel}>{t("surveyManagement.close.message")}</label>
          <TextArea
            value={form.closedMessage}
            onChange={(e) => setField("closedMessage", e.target.value)}
            placeholder={t("surveyManagement.public.closed_default")}
            autoSize={{ minRows: 2, maxRows: 5 }}
            maxLength={1000}
          />

          <div className={styles.settingRow}>
            <span className={styles.fieldLabel}>{t("surveyManagement.close.show_deadline")}</span>
            <Switch checked={!!form.showDeadline} onChange={(checked) => setField("showDeadline", checked)} />
          </div>
          <div className={styles.settingRow}>
            <span className={styles.fieldLabel}>{t("surveyManagement.close.reminder")}</span>
            <Switch checked={!!form.closeReminder} onChange={(checked) => setField("closeReminder", checked)} />
          </div>
          <div className={styles.settingRow}>
            <div className={styles.settingRowText}>
              <span className={styles.fieldLabel}>{t("surveyManagement.close.grace")}</span>
              <span className={styles.fieldHint}>{t("surveyManagement.close.grace_hint")}</span>
            </div>
            <InputNumber
              min={0}
              max={1440}
              value={form.closeGraceMinutes ?? DEFAULT_GRACE_MINUTES}
              onChange={(value) => setField("closeGraceMinutes", value ?? DEFAULT_GRACE_MINUTES)}
              style={{ width: 100 }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CloseSettings;
